/**
 * Customer Path Tab Component - JavaScript Version
 * 
 * Handles custom path building and comparison with optimal solutions.
 * Extracted from the main ShortestPathPage.js file.
 */

import React, { useState, useEffect } from 'react';
import { useAlgorithm } from '../../hooks/useAlgorithm';

const CustomerPathTab = ({
  graphData,
  nodes,
  onNodeClick,
  // Path building state from parent
  customStartNode,
  setCustomStartNode,
  customEndNode,
  setCustomEndNode,
  customPath,
  setCustomPath,
  isBuildingPath,
  setIsBuildingPath,
  optimalPath,
  setOptimalPath,
  showOptimalPath,
  setShowOptimalPath
}) => {

  // Algorithm hook for calculating optimal path
  const {
    runAlgorithm,
    isLoading
  } = useAlgorithm();

  // Calculate optimal path when start and end nodes are selected
  useEffect(() => {
    if (customStartNode && customEndNode) {
      calculateOptimalPath();
    } else {
      setOptimalPath([]);
    }
  }, [customStartNode, customEndNode, graphData.nodes, graphData.edges]);

  // Helper functions
  const areNodesConnected = (node1Id, node2Id) => {
    return graphData.edges.some(edge => 
      (edge.from_ === node1Id && edge.to === node2Id) ||
      (edge.from_ === node2Id && edge.to === node1Id)
    );
  };

  const getConnectedNodes = (nodeId) => {
    return graphData.edges
      .filter(edge => edge.from_ === nodeId || edge.to === nodeId)
      .map(edge => edge.from_ === nodeId ? edge.to : edge.from_);
  };

  const startPathBuilding = () => {
    if (!customStartNode || !customEndNode) {
      alert('Please select both start and end nodes first.');
      return;
    }
    setIsBuildingPath(true);
    setCustomPath([customStartNode.id]);
  };

  const addNodeToPath = (nodeId) => {
    if (!isBuildingPath) return;
    
    const lastNodeId = customPath[customPath.length - 1];
    
    // Check if the node is connected to the last node in the path
    if (!areNodesConnected(lastNodeId, nodeId)) {
      const node = nodes.find(n => n.id === nodeId);
      alert(`Node ${node?.label} is not connected to the last node in your path.`);
      return;
    }
    
    // Check if we've reached the end node
    if (nodeId === customEndNode.id) {
      setCustomPath([...customPath, nodeId]);
      setIsBuildingPath(false);
      alert('Path completed!');
      return;
    }
    
    // Add node to path
    setCustomPath([...customPath, nodeId]);
  };

  const resetCustomPath = () => {
    setCustomPath([]);
    setIsBuildingPath(false);
  };

  // Calculate optimal path using Dijkstra's algorithm
  const calculateOptimalPath = async () => {
    if (!customStartNode || !customEndNode) return;
    
    console.log('Calculating optimal path...', {
      startNode: customStartNode,
      endNode: customEndNode,
      graphData: graphData
    });
    
    try {
      console.log('Calling runAlgorithm with:', {
        algorithm: 'dijkstra',
        nodes: graphData.nodes,
        edges: graphData.edges,
        start: customStartNode.id,
        end: customEndNode.id
      });
      
      const result = await runAlgorithm(
        'dijkstra',
        graphData.nodes,
        graphData.edges,
        customStartNode.id,
        customEndNode.id
      );
      
      console.log('Algorithm result:', result);
      console.log('Result steps:', result.steps);
      if (result.steps && result.steps.length > 0) {
        console.log('First step:', result.steps[0]);
      }
      
      if (result && result.success) {
        console.log('Optimal path found:', result.path);
        setOptimalPath(result.path);
      } else {
        console.log('No optimal path found or algorithm failed');
        setOptimalPath([]);
      }
    } catch (err) {
      console.error('Failed to calculate optimal path:', err);
      console.error('Error details:', {
        message: err.message,
        stack: err.stack,
        name: err.name
      });
      setOptimalPath([]);
    }
  };

  const handleNodeClick = (node) => {
    if (isBuildingPath) {
      addNodeToPath(node.id);
      return;
    }
    onNodeClick(node);
  };

  return (
    <div className="customer-path-tab">
      <div className="control-panel">
        <h3>Custom Path Builder</h3>
        
        <div className="node-selection">
          <h4>Select Start and End Nodes</h4>
          
          <div className="selection-group">
            <label htmlFor="start-node">Start Node:</label>
            <select 
              id="start-node"
              value={customStartNode?.id || ''}
              onChange={(e) => {
                const nodeId = parseInt(e.target.value);
                const node = nodes.find(n => n.id === nodeId);
                setCustomStartNode(node || null);
              }}
              className="node-select"
            >
              <option value="">Select start node...</option>
              {nodes.map(node => (
                <option key={node.id} value={node.id}>
                  {node.label} (ID: {node.id})
                </option>
              ))}
            </select>
          </div>
          
          <div className="selection-group">
            <label htmlFor="end-node">End Node:</label>
            <select 
              id="end-node"
              value={customEndNode?.id || ''}
              onChange={(e) => {
                const nodeId = parseInt(e.target.value);
                const node = nodes.find(n => n.id === nodeId);
                setCustomEndNode(node || null);
              }}
              className="node-select"
            >
              <option value="">Select end node...</option>
              {nodes.map(node => (
                <option key={node.id} value={node.id}>
                  {node.label} (ID: {node.id})
                </option>
              ))}
            </select>
          </div>
          
          {customStartNode && customEndNode && (
            <div className="selection-info">
              <p><strong>Selected Path:</strong> {customStartNode.label} → {customEndNode.label}</p>
            </div>
          )}
        </div>
        
        {/* Path Building Controls */}
        {customStartNode && customEndNode && (
          <div className="path-building">
            <h4>Build Your Path</h4>
            
            {!isBuildingPath ? (
              <div className="building-controls">
                <button 
                  className="start-building-btn"
                  onClick={startPathBuilding}
                >
                  Start Building Path
                </button>
                <p className="building-instructions">
                  Click "Start Building Path" then click on connected nodes to build your path from {customStartNode.label} to {customEndNode.label}.
                </p>
              </div>
            ) : (
              <div className="building-controls">
                <div className="building-status">
                  <p><strong>Building Path:</strong> Click on connected nodes to continue your path.</p>
                  <p><strong>Current Path:</strong> {customPath.map(id => {
                    const node = nodes.find(n => n.id === id);
                    return node ? node.label : id;
                  }).join(' → ')}</p>
                </div>
                
                {customPath.length > 1 && (
                  <div className="connected-nodes">
                    <p><strong>Next possible nodes:</strong></p>
                    <div className="node-list">
                      {getConnectedNodes(customPath[customPath.length - 1])
                        .filter(nodeId => !customPath.includes(nodeId))
                        .map(nodeId => {
                          const node = nodes.find(n => n.id === nodeId);
                          return (
                            <span key={nodeId} className="connected-node">
                              {node?.label || nodeId}
                            </span>
                          );
                        })}
                    </div>
                  </div>
                )}
                
                <button 
                  className="reset-path-btn"
                  onClick={resetCustomPath}
                >
                  Reset Path
                </button>
              </div>
            )}
            
            {customPath.length > 0 && !isBuildingPath && (
              <div className="path-completed">
                <h5>✅ Path Completed!</h5>
                <p><strong>Your Path:</strong> {customPath.map(id => {
                  const node = nodes.find(n => n.id === id);
                  return node ? node.label : id;
                }).join(' → ')}</p>
                <button 
                  className="reset-path-btn"
                  onClick={resetCustomPath}
                >
                  Build New Path
                </button>
              </div>
            )}
          </div>
        )}
        
        {/* Path Comparison */}
        {customStartNode && customEndNode && (customPath.length > 0 || optimalPath.length > 0) && (
          <div className="path-comparison">
            <h4>Path Comparison</h4>
            
            <div className="comparison-controls">
              <button 
                className={`toggle-btn ${showOptimalPath ? 'active' : ''}`}
                onClick={() => setShowOptimalPath(!showOptimalPath)}
              >
                {showOptimalPath ? 'Hide' : 'Show'} Optimal Solution
              </button>
            </div>
            
            <div className="comparison-results">
              {customPath.length > 0 && (
                <div className="path-result custom-path">
                  <h5>🎯 Your Custom Path</h5>
                  <p><strong>Path:</strong> {customPath.map(id => {
                    const node = nodes.find(n => n.id === id);
                    return node ? node.label : id;
                  }).join(' → ')}</p>
                  <p><strong>Length:</strong> {customPath.length - 1} edges</p>
                </div>
              )}
              
              {optimalPath.length > 0 && showOptimalPath && (
                <div className="path-result optimal-path">
                  <h5>⚡ Optimal Solution</h5>
                  <p><strong>Path:</strong> {optimalPath.map(id => {
                    const node = nodes.find(n => n.id === id);
                    return node ? node.label : id;
                  }).join(' → ')}</p>
                  <p><strong>Length:</strong> {optimalPath.length - 1} edges</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerPathTab;
